import { useEffect, useMemo, useState } from 'react'
import { Label, SegmentedControl, Switch } from '@teach/ui'
import { multiplyCredits } from '@/features/ai/lib/money'
import { courseTargets, type TargetRef } from '@/features/ai/images/lib/collectTargets'
import { langSlot, otherSlot, type ImageSlot } from '@/features/ai/images/lib/targetKeys'
import { useTargetSelection } from '@/features/ai/images/useTargetSelection'
import type { RowId } from '@/features/ai/images/useTargetSelection'
import type { ImageQuoteView } from '@/features/ai/useImageQuote'
import { hasTranslationRow } from '@/features/editor/lib/courseTranslation'
import type { CourseWithoutBlocks } from '@/types/course'
import type { Lesson } from '@/types/lesson'
import { seedLangFor } from '../seedContentLang'
import { useHasStartedTranslationRun } from '../useBlocksInRun'
import { CourseTargetRow } from './CourseTargetRow'
import { LessonTargetRow } from './LessonTargetRow'
import { TargetPickerFooter } from './TargetPickerFooter'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
  /** Уроки детали курса: они приходят без блоков, блоки тянет сама строка. */
  lessons: Omit<Lesson, 'blocks'>[]
  quote: Pick<ImageQuoteView, 'perImage' | 'maxTargets' | 'balance'>
  onPlan: (plan: string[]) => void
  /** Слот-КОЛОНКА, с которого открылись: из итога перевода приходит слот
   *  переведённого языка (у английского курса это `base`, а не `en`). */
  initialSlot?: ImageSlot
}

const NO_TARGETS: TargetRef[] = []
const EMPTY_PLAN: string[] = []

const drawnHint = (
  m: { drawnHint: (total: number, drawn: number) => string },
  targets: TargetRef[],
) => m.drawnHint(targets.length, targets.filter((t) => t.hasImage).length)

export const TargetPicker = ({ course, lessons, quote, onPlan, initialSlot }: Props) => {
  // Исходный слот — КОЛОНКА языка оригинала курса, и берётся он тем же
  // `seedLangFor`, которым редактор засевает язык правки: у английского курса
  // оригинал лежит в `*En`, поэтому его исходный слот — `en`, а второй — `base`.
  // Своей копии правила «какой слот второй» тут нет намеренно.
  const m = useMessages(messages)
  const sourceSlot = langSlot(seedLangFor(course))
  const secondSlot = otherSlot(sourceSlot)
  // Живые строки списка: план обязан пересекаться с ними, иначе цели урока,
  // исчезнувшего из курса, остаются в состоянии навсегда (строка размонтирована
  // и обнулить свои цели уже некому).
  const rowIds = useMemo<RowId[]>(
    () => ['banner', 'mascots', ...lessons.map((l) => `lesson:${l.id}` as const)],
    [lessons],
  )
  const selection = useTargetSelection(rowIds)
  const { setRowTargets } = selection
  const [wantedSlot, setWantedSlot] = useState<ImageSlot>(initialSlot ?? sourceSlot)
  // Второй язык доступен ровно тогда же, когда вкладка «Языки» показывает
  // строку перевода: ОДИН источник признака на весь редактор.
  const canUseSecondLang = hasTranslationRow(course, useHasStartedTranslationRun(course.id))
  // Выбор автора зажат доступностью, а не только погашенным переключателем:
  // `initialSlot` приходит снаружи и мог бы указать на несуществующий слот.
  const slot = canUseSecondLang ? wantedSlot : sourceSlot
  const slotPlan = useMemo(() => ({ slot, sourceSlot }), [slot, sourceSlot])
  const { banner, mascots } = useMemo(() => courseTargets(course, slotPlan), [course, slotPlan])

  // Подпись целей строкой — тот же приём, что в `LessonTargetRow`: массивы
  // пересоздаются, и по ссылке эффект бил бы бесконечно.
  const signature = [banner, ...mascots].map((t) => `${t?.key}|${t?.hasImage}`).join('~')
  useEffect(() => {
    // Исчезнувшая строка обязана исчезнуть и из плана: во втором слоте баннера
    // и маскотов НЕ существует, а удалять строку `useTargetSelection` не
    // умеет — отдаём ей пустой список целей. Иначе отмеченный в основном языке
    // баннер утёк бы и в план, и в цену уже второго языка.
    setRowTargets('banner', banner ? [banner] : NO_TARGETS)
    setRowTargets('mascots', mascots)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, setRowTargets])

  const overLimit = quote.maxTargets !== undefined && selection.planSize > quote.maxTargets
  // Превышение потолка гасит запуск ДО клика: наверх уходит пустой план, а не
  // список, который гарантированно отобьётся `422 too_many_image_targets`.
  const plan = overLimit ? EMPTY_PLAN : selection.plan
  // Тело в скобках: `onPlan` типизирован как `void`-функция, но родитель волен
  // вернуть значение, и React принял бы его за функцию очистки.
  useEffect(() => {
    onPlan(plan)
  }, [plan, onPlan])

  return (
    <div className="space-y-3">
      <SegmentedControl
        size="sm"
        value={slot}
        onChange={setWantedSlot}
        // Выбирать не из чего, пока второго языка у курса нет: гасим весь
        // переключатель, а причину объясняем подписью под ним.
        disabled={!canUseSecondLang}
        // Подписи вешаются на РОЛЬ слота, а не на имя колонки: у английского
        // курса «Основной язык» — это `en`.
        options={[
          { id: sourceSlot, label: m.sourceLang },
          { id: secondSlot, label: m.secondLang },
        ]}
      />
      {canUseSecondLang ? null : (
        <p className="text-xs text-muted-foreground">{m.secondLangLater}</p>
      )}
      {banner ? (
        <CourseTargetRow
          id="target-banner"
          label={m.courseBanner}
          hint={drawnHint(m, [banner])}
          checked={selection.selected.has('banner')}
          onToggle={() => selection.toggle('banner')}
        />
      ) : null}
      {mascots.length ? (
        <CourseTargetRow
          id="target-mascots"
          label={m.mascots}
          hint={drawnHint(m, mascots)}
          checked={selection.selected.has('mascots')}
          onToggle={() => selection.toggle('mascots')}
        />
      ) : null}
      {lessons.map((lesson) => (
        <LessonTargetRow
          key={lesson.id}
          lesson={lesson}
          slot={slotPlan}
          checked={selection.selected.has(`lesson:${lesson.id}`)}
          onToggle={() => selection.toggle(`lesson:${lesson.id}`)}
          onTargets={(targets) => setRowTargets(`lesson:${lesson.id}`, targets)}
        />
      ))}
      <div className="flex items-center gap-3">
        <Switch
          id="overwrite"
          checked={selection.overwrite}
          onCheckedChange={selection.setOverwrite}
        />
        <Label htmlFor="overwrite">{m.overwrite}</Label>
      </div>
      {selection.overwrite ? <p className="text-xs text-warning">{m.overwriteWarning}</p> : null}
      <TargetPickerFooter
        planSize={selection.planSize}
        cost={quote.perImage ? multiplyCredits(quote.perImage, selection.planSize) : null}
        balance={quote.balance}
        overBy={
          overLimit && quote.maxTargets !== undefined ? selection.planSize - quote.maxTargets : 0
        }
      />
    </div>
  )
}
